import { computed, inject, Injectable, signal } from '@angular/core';
import { UrlService } from '@libs/services/url.service';
import { ChannelMap, ChannelRequest, Channels, IChannelResponse } from '@libs/interfaces/channel.interface';
import { Observable, startWith, throwError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { channelMapper } from '@libs/helpers/helper.utils';
import { VerificationTypeEnum } from '@libs/configs/component-configs/countdown.component-config';
import { SESSION_STORAGE } from '@libs/shared/injections/window';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

export enum RECONNECT {
  ID = 'hiddenId',
  PHONE = 'hiddenPhone',
}

@Injectable({ providedIn: 'root' })
export class ChannelService {
  public channels = signal<Channels>(null);
  public channel = signal<VerificationTypeEnum>(VerificationTypeEnum.SMS);
  private url = inject(UrlService);
  private http = inject(HttpClient);
  private sessionStorage = inject(SESSION_STORAGE);
  public readonly hiddenId = signal<string>(this.sessionStorage.getItem(RECONNECT.ID));
  public readonly hiddenPhone = signal<string>(this.sessionStorage.getItem(RECONNECT.PHONE));
  private router = inject(Router);
  public readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );
  public readonly showLink = computed(() => Boolean(this.hiddenId() && this.currentUrl().includes('auth/login')));

  public getChannel(data: ChannelRequest): Observable<ChannelMap> {
    return this.http
      .get<IChannelResponse>(this.url.channelUrl, {
        params: new HttpParams({ fromObject: data as any }),
      })
      .pipe(
        map((response: IChannelResponse) => channelMapper(response)),
        tap((data: ChannelMap) => {
          this.channels.set(data.channels);
          this.channel.set(data.channel);
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  public setReconnectParam(param: { phone: string; callId: string }): void {
    this.sessionStorage.setItem(RECONNECT.PHONE, param.phone);
    this.sessionStorage.setItem(RECONNECT.ID, param.callId);
    this.hiddenPhone.set(param.phone);
    this.hiddenId.set(param.callId);
  }

  public resetReconnectParam(): void {
    this.sessionStorage.removeItem(RECONNECT.ID);
    this.sessionStorage.removeItem(RECONNECT.PHONE);
    this.hiddenId.set(null);
    this.hiddenPhone.set(null);
  }
}
